import { PosthogService } from './shared/services/posthog.service'
import { demoSession, readSessionUser, saveDemo } from './demo/demo-session'
import { HttpErrorResponse } from '@angular/common/http'
import { Component, ErrorHandler, OnInit, inject } from '@angular/core'
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterModule } from '@angular/router'
import { UpdatePromptComponent } from './shared/components/update-prompt/update-prompt.component'
import { AuthService } from './auth/auth.service'
import { ChunkLoadErrorService } from './shared/services/chunk-load-error.service'
import { ProfileService } from './shared/services/profile.service'
import { SharedService } from './shared/services/shared.service'

/** Shortest gap between two profile re-hydrations, in ms. */
const PROFILE_REFRESH_INTERVAL_MS = 60_000

@Component({
  standalone: true,
  imports: [RouterModule, UpdatePromptComponent],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private demoAnalytics = inject(PosthogService)
  private errorHandler = inject(ErrorHandler)
  private auth = inject(AuthService)
  private shared = inject(SharedService)
  private router = inject(Router)
  private profileService = inject(ProfileService)
  private chunkLoadErrorService = inject(ChunkLoadErrorService)

  showLoadingIndicator: boolean = true
  private lastProfileRefresh = 0
  // The full-screen spinner is bootstrap-only; in-app navigations get the
  // slim top progress bar rendered by the layout instead.
  private initialNavigationDone = false

  constructor() {
    // Sent after the demo's full-load entry, so leaving /demo cannot drop it.
    const demo = demoSession()
    if (demo && !demo.started) {
      demo.started = true
      saveDemo(demo)
      this.demoAnalytics.capture('demo_session_started', { entry: demo.entry, language: demo.profile.user.viewLanguage })
    }

    // Prevent back/forward cache from showing stale authenticated pages after logout
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (event.persisted && !this.auth.isLoggedIn()) {
        this.router.navigate(['/login'])
      }
    })

    this.router.events.subscribe((routerEvent: any) => {
      if (routerEvent instanceof NavigationStart) {
        if (!this.initialNavigationDone) {
          this.showLoadingIndicator = true
        }
        this.scrollToTop()

        // The public sandbox must not refresh or mutate an existing merchant session.
        if (!demoSession() && !/^\/demo(?:[/?#]|$)/.test(routerEvent.url) && localStorage.getItem('token')) this.refreshProfile()
      }

      if (routerEvent instanceof NavigationError) {
        this.showLoadingIndicator = false
        this.initialNavigationDone = true

        // Check if this is a chunk load error during lazy loading
        if (this.chunkLoadErrorService.isChunkLoadError(routerEvent.error)) {
          this.chunkLoadErrorService.handleChunkLoadError()
        }
      }

      if (routerEvent instanceof NavigationEnd && demoSession() && !/^\/demo(?:[/?#]|$)/.test(routerEvent.urlAfterRedirects)) {
        // Record ids contain digits and route names never do: `/orders/order-details/:id`.
        this.demoAnalytics.capture('demo_section_viewed', {
          route: routerEvent.urlAfterRedirects.split(/[?#]/)[0].replace(/\/[^/]*\d[^/]*/g, '/:id'),
        })
      }

      if (routerEvent instanceof NavigationEnd || routerEvent instanceof NavigationCancel) {
        this.showLoadingIndicator = false
        this.initialNavigationDone = true
        // Ensure scroll to top after navigation completes
        this.scrollToTop()
      }
    })
  }

  /**
   * Re-hydrate store / subscription state from the backend.
   *
   * This used to run on *every* navigation. Each response pushes new values
   * onto the auth subjects, and the side menu rebuilds itself from those — so
   * every route change reloaded the whole menu (and fired an extra request)
   * even though only the page content had changed. The data it refreshes
   * (plan, status, credits) changes on the scale of minutes, so throttle it;
   * flows that need it immediately — billing, store switching — already call
   * getProfile() themselves.
   */
  private refreshProfile(): void {
    const now = Date.now()
    if (now - this.lastProfileRefresh < PROFILE_REFRESH_INTERVAL_MS) return
    this.lastProfileRefresh = now
    this.profileService.getProfile().subscribe({
      next: (res: any) => {
        if (!res) return
        this.auth.setUser({ data: res?.result })
        // Here rather than on the profile page, because the accounts this matters
        // for are exactly the ones that never open it — see captureTimezone.
        this.profileService.captureTimezone(res?.result?.user)
      },
      error: (error: unknown) => {
        // Background re-hydration is best-effort during a connection outage.
        // Keep the cached session and the throttle; a later navigation retries.
        if (error instanceof HttpErrorResponse && error.status === 0) return
        this.errorHandler.handleError(error)
      },
    })
  }

  private scrollToTop(): void {
    // Scroll the window
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })

    // Also scroll any scrollable containers
    document.querySelectorAll('[data-scrollable]').forEach(element => (element.scrollTop = 0))

    // Scroll document element and body
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }

  ngOnInit(): void {
    this.getUserData()
    // Start from what this device last showed; `setUser` applies the account's
    // saved mode as soon as the profile arrives.
    this.shared.applyThemeMode(this.shared.getThemeMode())
  }

  getUserData(): void {
    if (readSessionUser()) {
      const user = JSON.parse(readSessionUser() || '{}')
      this.auth.user.next(user?.data?.user)
    }
  }
}
